import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Check, X, Loader2, ChevronRight } from 'lucide-react';
import {
  useWishlists,
  useCreateWishlist,
  useUpdateWishlist,
  useDeleteWishlist,
} from '../hooks/useWishlists';

export default function WishlistList() {
  const { data: wishlists, isLoading, isError } = useWishlists();
  const createWishlist = useCreateWishlist();
  const updateWishlist = useUpdateWishlist();
  const deleteWishlist = useDeleteWishlist();

  const navigate = useNavigate();
  const [newName, setNewName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const handleCreate = (e: FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    createWishlist.mutate(newName.trim(), {
      onSuccess: () => {
        setNewName('');
        setIsAdding(false);
      },
    });
  };

  const startEdit = (id: string, name: string) => {
    setEditingId(id);
    setEditName(name);
  };

  const handleUpdate = (id: string) => {
    if (!editName.trim()) return;
    updateWishlist.mutate(
      { id, name: editName.trim() },
      { onSuccess: () => setEditingId(null) }
    );
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 flex items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading wishlists...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-border bg-card p-6">
        <p className="text-sm text-destructive">Failed to load wishlists.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between p-6 pb-3">
        <h3 className="text-lg font-medium text-card-foreground">Wishlists</h3>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus className="h-4 w-4" />
            New
          </button>
        )}
      </div>

      {isAdding && (
        <form onSubmit={handleCreate} className="flex items-center gap-2 px-6 py-3 border-b border-border">
          <input
            type="text"
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Wishlist name"
            className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
          <button
            type="submit"
            disabled={createWishlist.isPending || !newName.trim()}
            className="rounded-md p-2 text-green-600 hover:bg-green-500/10 disabled:opacity-50"
          >
            {createWishlist.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => { setIsAdding(false); setNewName(''); }}
            className="rounded-md p-2 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </form>
      )}

      <div className="divide-y divide-border">
        {wishlists && wishlists.length > 0 ? (
          wishlists.map((wl) => (
            <div key={wl.id} className="flex items-center justify-between px-6 py-3">
              {editingId === wl.id ? (
                <div className="flex items-center gap-2 flex-1 mr-2">
                  <input
                    type="text"
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleUpdate(wl.id);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                  <button
                    onClick={() => handleUpdate(wl.id)}
                    disabled={updateWishlist.isPending || !editName.trim()}
                    className="rounded-md p-2 text-green-600 hover:bg-green-500/10 disabled:opacity-50"
                  >
                    {updateWishlist.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="rounded-md p-2 text-muted-foreground hover:bg-muted"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => navigate(`/wishlists/${wl.id}`)}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-80 transition-opacity"
                  >
                    <span className="text-sm font-medium text-card-foreground truncate">
                      {wl.name}
                    </span>
                    {wl.is_default && (
                      <span className="inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        Default
                      </span>
                    )}
                    {wl.product_count != null && (
                      <span className="text-xs text-muted-foreground">
                        {wl.product_count} {wl.product_count === 1 ? 'item' : 'items'}
                      </span>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto flex-shrink-0" />
                  </button>
                  <div className="flex items-center gap-1 ml-2">
                    <button
                      onClick={() => startEdit(wl.id, wl.name)}
                      className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label={`Rename ${wl.name}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    {!wl.is_default && (
                      <button
                        onClick={() => deleteWishlist.mutate(wl.id)}
                        disabled={deleteWishlist.isPending}
                        className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        aria-label={`Delete ${wl.name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))
        ) : (
          <div className="px-6 py-4">
            <p className="text-sm text-muted-foreground">No wishlists yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}
